import {
  selectVendorBooths,
  selectVendorBoothsPageInfo,
  selectVendorBoothsStatus,
} from '@app/store/selectors';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { ListLoadingSkelton } from './list-loading-skelton';

type VendorBoothsListProps = {
  onSelectVendorBooth: (selectedVendorBooth: {
    id: string;
    name: string;
  }) => void;
  scrollableRoot?: HTMLElement | null;
};

export const VendorBoothsList = ({
  onSelectVendorBooth,
  scrollableRoot,
}: VendorBoothsListProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const vendorBooths = useAppSelector(selectVendorBooths);
  const vendorBoothsStatus = useAppSelector(selectVendorBoothsStatus);
  const vendorBoothsPageInfo = useAppSelector(selectVendorBoothsPageInfo);

  const { ref: loadingMoreRef, inView: loadingMoreInView } = useInView({
    root: scrollableRoot ?? null,
    rootMargin: '0px 0px 250px 0px',
    threshold: 0,
  });

  useEffect(() => {
    const shouldSkipLoading =
      !loadingMoreInView ||
      !vendorBoothsPageInfo.hasNextPage ||
      vendorBoothsStatus === 'processing';

    if (shouldSkipLoading) {
      return;
    }

    dispatch({
      type: '[nav-bar] vendor-booths fetch',
    });
  }, [
    loadingMoreInView,
    vendorBoothsPageInfo.hasNextPage,
    vendorBoothsStatus,
    dispatch,
  ]);

  if (
    (vendorBooths?.length === 0 && vendorBoothsStatus !== 'processing') ||
    vendorBoothsStatus === 'failed'
  ) {
    return null;
  }

  return (
    <div role="group" aria-label={t('orgSwitcher.vendorBooths')}>
      <div className="mb-2 text-xs font-inherit text-[rgb(105,110,124)]  uppercase tracking-wider">
        {t('orgSwitcher.vendorBooths')}
      </div>
      {vendorBooths?.map(booth => (
        <button
          key={booth.id}
          type="button"
          className="flex items-center bg-white text-left rounded font-medium mb-2 p-2 cursor-pointer w-full hover:bg-[rgb(236,243,248)] hover:text-[rgb(29,110,170)] min-h-[56px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          onClick={() =>
            onSelectVendorBooth({ id: booth.id, name: booth.name })
          }
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectVendorBooth({ id: booth.id, name: booth.name });
            }
          }}
          role="menuitem"
          aria-label={t('orgSwitcher.accessibility.vendorBooths', {
            vendorBoothName: booth.name,
          })}
          aria-describedby={
            booth.eventName ? `booth-event-${booth.id}` : undefined
          }
        >
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium mr-3">
            {booth.image ? (
              <img
                src={booth.image}
                alt={booth.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              booth.name.charAt(0).toUpperCase()
            )}
          </div>
          <div className="flex flex-col">
            <div className="max-w-[184px] text-sm font-medium text-gray-900 truncate">
              {booth.name}
            </div>
            {booth.eventName && (
              <div
                id={`booth-event-${booth.id}`}
                className="max-w-[184px] text-xs text-[rgb(105,110,124)] font-normal truncate"
              >
                {booth.eventName}
              </div>
            )}
          </div>
        </button>
      ))}
      {vendorBoothsStatus === 'processing' ? (
        <div
          className="mb-2"
          role="status"
          aria-label={t('orgSwitcher.accessibility.loadingVendorBooths')}
        >
          <ListLoadingSkelton />
          <ListLoadingSkelton />
          <ListLoadingSkelton />
        </div>
      ) : null}
      {vendorBoothsPageInfo.hasNextPage &&
        vendorBoothsStatus !== 'processing' && (
          <div className="h-4 w-full" ref={loadingMoreRef}></div>
        )}
    </div>
  );
};
